import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PriModal from '../PriModal';
import PriAutocompleteSelect from '../../common/PriAutocompleteSelect';
import TableSkeleton from '../../loaders/TableSkeleton';
import WorkingDaysService from '../../../services/WorkingDaysService';
import '../../../styles/WorkingDays.css';
import { useTranslation } from 'react-i18next';

const MONTH_OPTIONS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const normalizeMonths = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value
      .map((item) => typeof item === 'string' ? item.trim() : '')
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return normalizeMonths(parsed);
      }
    } catch (error) {
      // ignore JSON parsing error and fall back to comma-separated parsing
    }

    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const getDisplaySchoolName = (school, t) => {
  if (!school) return t('unknownSchool');

  if (typeof school === 'string') return school;
  if (school.schoolName) return school.schoolName;
  if (school.name) return school.name;
  if (school.school?.name) return school.school.name;
  return t('workingDaysSchoolNumber', { id: school.id ?? school.schoolId ?? '' });
};

const getDisplayAcademicYear = (item, t) => {
  if (!item) return t('notAvailable');

  if (typeof item === 'string') return item;
  if (item.year) return item.year;
  if (item.name) return item.name;
  if (item.academicYear?.year) return item.academicYear.year;
  if (item.academicYear?.name) return item.academicYear.name;
  if (item.academicYearName) return item.academicYearName;
  return t('workingDaysYearNumber', { id: item.academicYearId ?? item.id ?? '' });
};

const toNumberOrNull = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const defaultForm = {
  schoolId: '',
  academicYearId: '',
  totalWorkingDays: '',
  months: [],
  active: true,
};

const formatMonthSummary = (months, t, language) => {
  const validMonths = normalizeMonths(months);

  if (!validMonths.length) {
    return t('workingDaysNoMonthsSelected');
  }

  const localizedMonths = validMonths.map((month) => {
    const monthIndex = MONTH_OPTIONS.indexOf(month);
    return monthIndex === -1
      ? month
      : new Intl.DateTimeFormat(language, { month: 'long', timeZone: 'UTC' })
        .format(new Date(Date.UTC(2020, monthIndex, 1)));
  });

  if (localizedMonths.length <= 3) {
    return localizedMonths.join(' · ');
  }

  return t('workingDaysMonthSummary', {
    months: localizedMonths.slice(0, 3).join(' · '),
    count: localizedMonths.length - 3,
  });
};

export default function WorkingDaysPage({ schoolId, academicYearId, academicYearName, user, onToast }) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language || 'en';
  const loggedInSchoolId = user?.schoolId || user?.school?.id || schoolId || localStorage.getItem('ss_school_id');
  const parsedLoggedInSchoolId = Number(loggedInSchoolId);
  const defaultSchoolId = Number.isFinite(parsedLoggedInSchoolId) && parsedLoggedInSchoolId > 0
    ? parsedLoggedInSchoolId
    : '';
  const loggedInSchoolName = user?.schoolName || user?.school?.name || localStorage.getItem('ss_school_name');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [academicYearFilter, setAcademicYearFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const onToastRef = useRef(onToast);

  useEffect(() => {
    onToastRef.current = onToast;
  }, [onToast]);

  const academicYearOptions = useMemo(() => {
    const collection = [];

    records.forEach((item) => {
      const yearId = item?.academicYearId ?? item?.academicYear?.id;
      const yearName = getDisplayAcademicYear(item, t);

      if (yearId && !collection.some((entry) => String(entry.id) === String(yearId))) {
        collection.push({ id: Number(yearId), name: yearName });
      }
    });

    const fallbackYearId = user?.academicYearId || academicYearId || localStorage.getItem('ss_academic_year_id');
    const fallbackYearName = user?.academicYear || academicYearName || localStorage.getItem('ss_academic_year');

    if (fallbackYearId && !collection.some((entry) => String(entry.id) === String(fallbackYearId))) {
      collection.push({ id: Number(fallbackYearId), name: fallbackYearName || t('workingDaysYearNumber', { id: fallbackYearId }) });
    }

    return collection.sort((a, b) => {
      if (Number.isFinite(a.id) && Number.isFinite(b.id)) {
        return Number(b.id) - Number(a.id);
      }
      return String(a.name).localeCompare(String(b.name));
    });
  }, [academicYearId, academicYearName, records, t, user]);

  const loadWorkingDays = useCallback(async () => {
    setLoading(true);

    try {
      const response = await WorkingDaysService.getAll();
      const list = Array.isArray(response) ? response : response?.content || [];

      const normalized = list.map((item) => ({
        ...item,
        schoolId: item?.schoolId ?? item?.school?.id ?? null,
        academicYearId: item?.academicYearId ?? item?.academicYear?.id ?? null,
        totalWorkingDays: toNumberOrNull(item?.totalWorkingDays),
        months: normalizeMonths(item?.months),
        active: item?.active ?? true,
      }));

      setRecords(normalized);
      setLoadError('');
    } catch (error) {
      console.error('Working Days load error', error);
      const message = error?.response?.data?.message || error?.response?.data?.error || t('workingDaysLoadFailed');
      setLoadError(message);
      onToastRef.current?.(message, 'error');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadWorkingDays();
  }, [loadWorkingDays]);

  const filteredRecords = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return records.filter((record) => {
      const schoolMatch = defaultSchoolId > 0 && String(record.schoolId) === String(defaultSchoolId);
      const yearMatch = academicYearFilter === 'all' || String(record.academicYearId) === String(academicYearFilter);
      const statusMatch = statusFilter === 'all'
        || (statusFilter === 'active' && record.active === true)
        || (statusFilter === 'inactive' && record.active === false);

      const schoolName = getDisplaySchoolName(record?.school ?? record, t);
      const academicYearName = getDisplayAcademicYear(record, t);
      const searchMatch = !query || [
        schoolName,
        academicYearName,
        String(record.totalWorkingDays || ''),
        record.months.join(' '),
      ].some((value) => String(value).toLowerCase().includes(query));

      return schoolMatch && yearMatch && statusMatch && searchMatch;
    });
  }, [academicYearFilter, defaultSchoolId, records, searchQuery, statusFilter, t]);

  const openCreateModal = () => {
    setEditingId(null);
    setErrors({});
    setForm({
      ...defaultForm,
      schoolId: defaultSchoolId,
      academicYearId: user?.academicYearId || academicYearId || localStorage.getItem('ss_academic_year_id') || '',
      active: true,
    });
    setShowForm(true);
  };

  const openEditModal = (record) => {
    setEditingId(record.id);
    setErrors({});
    setForm({
      schoolId: record.schoolId || '',
      academicYearId: record.academicYearId || '',
      totalWorkingDays: record.totalWorkingDays ?? '',
      months: [...record.months],
      active: Boolean(record.active),
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setErrors({});
    setEditingId(null);
    setForm(defaultForm);
  };

  const handleSubmit = async () => {
    const nextErrors = {};

    if (!form.schoolId) nextErrors.schoolId = t('workingDaysSchoolRequired');
    if (!form.academicYearId) nextErrors.academicYearId = t('workingDaysAcademicYearRequired');
    if (!form.totalWorkingDays || Number(form.totalWorkingDays) <= 0) nextErrors.totalWorkingDays = t('workingDaysTotalRequired');
    if (!Array.isArray(form.months) || form.months.length === 0) nextErrors.months = t('workingDaysSelectMonthRequired');

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    const payload = {
      schoolId: Number(form.schoolId),
      academicYearId: Number(form.academicYearId),
      totalWorkingDays: Number(form.totalWorkingDays),
      months: form.months,
      active: Boolean(form.active),
    };

    setSaving(true);

    try {
      if (editingId) {
        await WorkingDaysService.update(editingId, payload);
        onToast?.(t('workingDaysUpdated'), 'success');
      } else {
        await WorkingDaysService.add(payload);
        onToast?.(t('workingDaysAdded'), 'success');
      }

      closeForm();
      await loadWorkingDays();
    } catch (error) {
      const message = error?.response?.data?.message || error?.response?.data?.error || error?.response?.data?.detail || error?.message || t('workingDaysSaveFailed');
      onToast?.(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    setDeleting(true);

    try {
      await WorkingDaysService.delete(deleteTarget.id);
      onToast?.(t('workingDaysDeleted'), 'success');
      setDeleteTarget(null);
      await loadWorkingDays();
    } catch (error) {
      const message = error?.response?.data?.message || error?.response?.data?.error || error?.response?.data?.detail || error?.message || t('workingDaysDeleteFailed');
      onToast?.(message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  const toggleMonth = (month) => {
    setForm((prev) => {
      const months = prev.months.includes(month)
        ? prev.months.filter((item) => item !== month)
        : [...prev.months, month];

      return { ...prev, months };
    });

    setErrors((prev) => ({ ...prev, months: '' }));
  };

  const formSchoolName = (String(form.schoolId) === String(defaultSchoolId) ? loggedInSchoolName : '')
    || (form.schoolId ? t('workingDaysSchoolNumber', { id: form.schoolId }) : t('workingDaysSelectSchool'));

  return (
    <div className="working-days-page">
      <div className="working-days-header">
        <div>
          <h1>{t('workingDaysTitle')}</h1>
          <p>{t('workingDaysDescription')}</p>
        </div>

        <button className="pri-btn pri-btn-primary" onClick={openCreateModal}>
          + {t('workingDaysAdd')}
        </button>
      </div>

      <div className="working-days-filters pri-card">
        <div className="working-days-filter-grid">
          <div className="working-days-form-group">
            <label>{t('school')}</label>
            <input type="text" value={loggedInSchoolName || (defaultSchoolId ? t('workingDaysSchoolNumber', { id: defaultSchoolId }) : t('unknownSchool'))} readOnly aria-readonly="true" />
          </div>

          <div className="working-days-form-group">
            <label>{t('academicYear')}</label>
            <select value={academicYearFilter} onChange={(event) => setAcademicYearFilter(event.target.value)}>
              <option value="all">{t('workingDaysAllYears')}</option>
              {academicYearOptions.map((option) => (
                <option key={option.id} value={option.id}>{option.name}</option>
              ))}
            </select>
          </div>

          <div className="working-days-form-group">
            <label>{t('status')}</label>
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              <option value="all">{t('all')}</option>
              <option value="active">{t('active')}</option>
              <option value="inactive">{t('inactive')}</option>
            </select>
          </div>

          <div className="working-days-form-group working-days-search">
            <label>{t('search')}</label>
            <input
              value={searchQuery}
              placeholder={t('workingDaysSearchPlaceholder')}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="pri-card working-days-table-card">
        {loading ? (
          <TableSkeleton rows={5} columns={6} />
        ) : loadError ? (
          <div className="working-days-load-error" role="alert">
            <h3>{t('workingDaysUnableToLoad')}</h3>
            <p>{loadError}</p>
            <button className="pri-btn pri-btn-light" onClick={loadWorkingDays}>
              {t('retry')}
            </button>
          </div>
        ) : filteredRecords.length === 0 ? (
          <div className="working-days-empty-state">
            <div className="working-days-empty-icon">📅</div>
            <h3>{t('workingDaysNoRecords')}</h3>
            <p>{t('workingDaysNoConfiguration')}</p>
            <button className="pri-btn pri-btn-primary" onClick={openCreateModal}>
              + {t('workingDaysAdd')}
            </button>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="pri-table working-days-table">
              <thead>
                <tr>
                  <th>{t('school')}</th>
                  <th>{t('academicYear')}</th>
                  <th>{t('workingDaysTotal')}</th>
                  <th>{t('months')}</th>
                  <th>{t('status')}</th>
                  <th>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.map((record) => (
                  <tr key={record.id}>
                    <td>{getDisplaySchoolName(record?.school ?? record, t)}</td>
                    <td>{getDisplayAcademicYear(record, t)}</td>
                    <td>{record.totalWorkingDays ?? 0}</td>
                    <td>
                      <div className="working-days-months">
                        {formatMonthSummary(record.months, t, language)}
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${record.active ? 'status-active' : 'status-inactive'}`}>
                        {record.active ? t('active') : t('inactive')}
                      </span>
                    </td>
                    <td>
                      <div className="working-days-actions">
                        <button className="pri-btn pri-btn-light" onClick={() => openEditModal(record)}>
                          {t('edit')}
                        </button>
                        <button className="pri-btn pri-btn-danger" onClick={() => setDeleteTarget(record)}>
                          {t('delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <PriModal
          title={editingId ? t('workingDaysEditTitle') : t('workingDaysAddTitle')}
          onClose={closeForm}
          wide
          footer={
            <div className="working-days-modal-footer">
              <button className="pri-btn pri-btn-ghost" onClick={closeForm} disabled={saving}>
                {t('cancel')}
              </button>
              <button className="pri-btn pri-btn-primary" onClick={handleSubmit} disabled={saving}>
                {saving
                  ? t('saving')
                  : editingId
                    ? t('workingDaysUpdate')
                    : t('workingDaysCreate')}
              </button>
            </div>
          }
        >
          <div className="working-days-form-grid">
            <div className="working-days-form-group">
              <label>{t('school')} *</label>
              <input type="text" value={formSchoolName} readOnly aria-readonly="true" />
              {errors.schoolId && <span className="pri-error-message">{errors.schoolId}</span>}
            </div>

            <div className="working-days-form-group">
              <label>{t('academicYear')} *</label>
              <PriAutocompleteSelect
                value={academicYearOptions.find((year) => Number(year.id) === Number(form.academicYearId)) || null}
                data={academicYearOptions}
                onChange={(value) => {
                  setForm((prev) => ({ ...prev, academicYearId: value?.id ?? '' }));
                  setErrors((prev) => ({ ...prev, academicYearId: '' }));
                }}
                placeholder={t('workingDaysSelectAcademicYear')}
                optionLabel="name"
                optionValue="id"
                clearable={false}
                fullWidth
                size="small"
              />
              {errors.academicYearId && <span className="pri-error-message">{errors.academicYearId}</span>}
            </div>

            <div className="working-days-form-group">
              <label>{t('workingDaysTotal')} *</label>
              <input
                type="number"
                min="1"
                value={form.totalWorkingDays}
                onChange={(event) => {
                  setForm((prev) => ({ ...prev, totalWorkingDays: event.target.value }));
                  setErrors((prev) => ({ ...prev, totalWorkingDays: '' }));
                }}
                placeholder="220"
              />
              {errors.totalWorkingDays && <span className="pri-error-message">{errors.totalWorkingDays}</span>}
            </div>

            <div className="working-days-form-group working-days-form-full">
              <label>{t('months')} *</label>
              <div className="working-days-month-picker">
                {MONTH_OPTIONS.map((month) => (
                  <button
                    type="button"
                    key={month}
                    className={`month-option ${form.months.includes(month) ? 'selected' : ''}`}
                    onClick={() => toggleMonth(month)}
                  >
                    {new Intl.DateTimeFormat(language, { month: 'long', timeZone: 'UTC' })
                      .format(new Date(Date.UTC(2020, MONTH_OPTIONS.indexOf(month), 1)))}
                  </button>
                ))}
              </div>
              {errors.months && <span className="pri-error-message">{errors.months}</span>}
            </div>

            <div className="working-days-form-group working-days-form-full">
              <label className="working-days-toggle-label">
                <span>{t('active')}</span>
                <input
                  type="checkbox"
                  checked={Boolean(form.active)}
                  onChange={(event) => setForm((prev) => ({ ...prev, active: event.target.checked }))}
                />
              </label>
            </div>
          </div>
        </PriModal>
      )}

      {deleteTarget && (
        <PriModal
          title={t('workingDaysDeleteTitle')}
          onClose={() => setDeleteTarget(null)}
          danger
          footer={
            <div className="working-days-modal-footer">
              <button className="pri-btn pri-btn-ghost" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                {t('cancel')}
              </button>
              <button className="pri-btn pri-btn-danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? t('deleting') : t('delete')}
              </button>
            </div>
          }
        >
          <div className="working-days-delete-body">
            <p>{t('workingDaysDeleteConfirm')}</p>
          </div>
        </PriModal>
      )}
    </div>
  );
}
